import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  BarChart3,
  Play,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Calendar,
  Music,
  Building2,
  Users,
  Server,
  Database,
  Activity,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card";
import { adminApi } from "../../utils/api";
import CleanupAction from "./components/CleanupAction";
import { TranslationCoverage } from "./TranslationCoverage";

const HEALTH_REFRESH_INTERVAL_MS = 30_000;

function ServiceStatus({ label, icon: Icon, status }) {
  const ok = status === "connected";
  const warning = status === "not_configured" || status === "unknown";
  const statusClass = ok ? "text-green-700" : warning ? "text-yellow-700" : "text-red-700";
  const statusLabel =
    status === "connected"
      ? "Connected"
      : status === "unreachable"
        ? "Unreachable"
        : status === "not_configured"
          ? "Not configured"
          : status;
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Icon className="h-4 w-4 text-muted-foreground" />
        {label}
      </div>
      <div className={`flex items-center gap-1.5 text-sm font-medium ${statusClass}`}>
        {ok ? <CheckCircle2 className="h-4 w-4" /> : warning ? <AlertTriangle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
        {statusLabel}
      </div>
    </div>
  );
}

export default function SystemPage() {
  // --- System health ---
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const healthIntervalRef = useRef(null);

  // --- Stats ---
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  // --- Admin actions feedback ---
  const [actionMessage, setActionMessage] = useState(null);
  const [actionError, setActionError] = useState(null);

  // --- Pipeline ---
  const [isPipelineRunning, setIsPipelineRunning] = useState(false);
  const [isQcRunning, setIsQcRunning] = useState(false);
  const qcPollRef = useRef(null);

  const stopQcPoll = useCallback(() => {
    if (qcPollRef.current) {
      clearInterval(qcPollRef.current);
      qcPollRef.current = null;
    }
  }, []);

  const startQcPoll = useCallback((jobId) => {
    stopQcPoll();
    qcPollRef.current = setInterval(async () => {
      try {
        const job = await adminApi.getJobStatus(jobId);
        if (job.status === "succeeded" || job.status === "failed") {
          stopQcPoll();
          setIsQcRunning(false);
          if (job.status === "failed") {
            setActionError("Quality check job failed. Check logs for details.");
          } else {
            setActionMessage("✓ Quality checks completed.");
          }
        }
      } catch {
        // keep polling
      }
    }, 10000);
  }, [stopQcPoll]);

  useEffect(() => () => stopQcPoll(), [stopQcPoll]);
  const [pipelineMode, setPipelineMode] = useState(null); // "n8n" | "api"
  const [pipelineJobId, setPipelineJobId] = useState(null);
  const [pipelineExecutionUrl, setPipelineExecutionUrl] = useState(null);
  const [pipelineExecutionId, setPipelineExecutionId] = useState(null);
  const pipelinePollRef = useRef(null);

  const stopPipelinePoll = useCallback(() => {
    if (pipelinePollRef.current) {
      clearInterval(pipelinePollRef.current);
      pipelinePollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPipelinePoll(), [stopPipelinePoll]);

  // Auto-dismiss feedback
  useEffect(() => {
    if (!actionMessage) return;
    const t = setTimeout(() => setActionMessage(null), 8000);
    return () => clearTimeout(t);
  }, [actionMessage]);

  useEffect(() => {
    if (!actionError) return;
    const t = setTimeout(() => setActionError(null), 6000);
    return () => clearTimeout(t);
  }, [actionError]);

  // Health polling
  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const data = await adminApi.getHealth();
      setHealth(data);
    } catch {
      setHealth({ status: "degraded", elasticsearch: "unreachable", postgresql: "unreachable", n8n: "unreachable" });
    } finally {
      setHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHealth();
    healthIntervalRef.current = setInterval(loadHealth, HEALTH_REFRESH_INTERVAL_MS);
    return () => clearInterval(healthIntervalRef.current);
  }, [loadHealth]);

  // Stats
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const data = await adminApi.getStats();
      setStats(data);
    } catch (err) {
      setStatsError(err.response?.data?.detail || "Error loading statistics");
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const startApiPipelinePoll = useCallback((jobId) => {
    stopPipelinePoll();
    pipelinePollRef.current = setInterval(async () => {
      try {
        const job = await adminApi.getJobStatus(jobId);
        if (job.status === "succeeded") {
          stopPipelinePoll();
          setIsPipelineRunning(false);
          setPipelineJobId(null);
          setActionMessage("Pipeline completed successfully.");
          loadStats();
        } else if (job.status === "failed") {
          stopPipelinePoll();
          setIsPipelineRunning(false);
          setPipelineJobId(null);
          setActionError(`Pipeline failed: ${job.error || "Unknown error"}`);
        }
      } catch {
        // polling error — keep trying
      }
    }, 10000);
    // loadStats is stable here because it's defined with useCallback([]).
  }, [loadStats, stopPipelinePoll]);

  const handleRunPipeline = async () => {
    setIsPipelineRunning(true);
    setActionMessage(null);
    setActionError(null);
    setPipelineMode(null);
    setPipelineJobId(null);
    setPipelineExecutionUrl(null);
    setPipelineExecutionId(null);

    try {
      const pipelineStart = await adminApi.runPipeline();
      if (pipelineStart.mode === "n8n") {
        setPipelineMode("n8n");
        setPipelineExecutionUrl(pipelineStart.execution_url || null);
        setPipelineExecutionId(pipelineStart.execution_id || null);
        setActionMessage(pipelineStart.message || "Pipeline started via n8n.");
        return;
      }
      if (pipelineStart.mode === "api" && pipelineStart.job_id) {
        setPipelineMode("api");
        setPipelineJobId(pipelineStart.job_id);
        setActionMessage(pipelineStart.message || "Pipeline running directly via API.");
        startApiPipelinePoll(pipelineStart.job_id);
        return;
      }
      setIsPipelineRunning(false);
      setActionError("Pipeline trigger returned an unexpected response.");
    } catch (err) {
      setIsPipelineRunning(false);
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        const msg = typeof detail === "object" ? detail.message : detail;
        setActionError(msg || "A pipeline job is already running. Check back later.");
      } else {
        setActionError(typeof detail === "string" ? detail : "Failed to start pipeline.");
      }
    }
  };

  const handleRunQualityCheck = async () => {
    setIsQcRunning(true);
    setActionMessage(null);
    setActionError(null);
    try {
      const result = await adminApi.runQualityCheck();
      if (result.mode === "n8n") {
        // n8n response has no job_id — polling is not applicable.
        setActionMessage(result.message || "Quality checks triggered via n8n.");
        setIsQcRunning(false);
      } else {
        // Use the API message — it already says "n8n unavailable" when n8n was bypassed.
        setActionMessage(result.message || "Quality checks started…");
        startQcPoll(result.job_id);
      }
    } catch (err) {
      setIsQcRunning(false);
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        const msg = typeof detail === "object" ? detail.message : detail;
        setActionError(msg || "A quality check job is already running.");
      } else {
        setActionError(typeof detail === "string" ? detail : "Failed to start quality check.");
      }
    }
  };

  const statusItems = health
    ? [
        { label: "API", icon: Activity, status: "connected" },
        { label: "Elasticsearch", icon: Database, status: health.elasticsearch || "unknown" },
        { label: "PostgreSQL", icon: Server, status: health.postgresql || "unknown" },
        { label: "n8n", icon: Play, status: health.n8n || "unknown" },
      ]
    : [];

  const entityStats = stats
    ? [
        { label: "Events", value: stats.events?.total ?? "-", icon: Calendar, color: "text-blue-700" },
        { label: "Artists", value: stats.artists?.total ?? "-", icon: Music, color: "text-purple-700" },
        { label: "Venues", value: stats.venues?.total ?? "-", icon: Building2, color: "text-green-700" },
        { label: "Users", value: stats.users?.total ?? "-", icon: Users, color: "text-orange-700" },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">System</h2>
        <p className="text-muted-foreground">Infrastructure status, statistics, and admin operations</p>
      </div>

      <div className="grid gap-6">
        {/* System Status */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-muted-foreground" />
                <CardTitle>System Status</CardTitle>
              </div>
              <Button variant="outline" size="sm" onClick={loadHealth} disabled={healthLoading}>
                {healthLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            </div>
            {health && (
              <p className="text-sm text-muted-foreground">
                Overall:{" "}
                <span className={health.status === "healthy" ? "text-green-700 font-medium" : "text-red-700 font-medium"}>
                  {health.status === "healthy" ? "Healthy" : "Degraded"}
                </span>
                {" · "}Auto-refreshes every 30s
              </p>
            )}
          </CardHeader>
          <CardContent>
            {healthLoading && !health ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="divide-y divide-border">
                {statusItems.map((item) => (
                  <ServiceStatus key={item.label} {...item} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Statistics */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Statistics</CardTitle>
              </div>
              <Button variant="outline" size="sm" onClick={loadStats} disabled={statsLoading}>
                {statsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {statsError ? (
              <div className="p-3 rounded-md text-sm bg-red-500/10 text-red-700 border border-red-500/20">{statsError}</div>
            ) : statsLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {/* Entity totals */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {entityStats.map((stat) => (
                    <div key={stat.label} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <stat.icon className={`h-8 w-8 ${stat.color}`} />
                      <div>
                        <p className="text-2xl font-bold">{stat.value?.toLocaleString?.() ?? stat.value}</p>
                        <p className="text-xs text-muted-foreground">{stat.label}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Event status breakdown */}
                {stats?.events && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Events by status</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {[
                        { key: "active", label: "Active", color: "text-green-700" },
                        { key: "pending", label: "Pending", color: "text-yellow-700" },
                        // Colors match EventStatusBadge semantics.
                        { key: "duplicate", label: "Duplicate", color: "text-red-700" },
                        { key: "hidden", label: "Hidden", color: "text-slate-700" },
                        { key: "multi_occurrence", label: "Multi-date", color: "text-blue-700" },
                      ].map(({ key, label, color }) => (
                        <div key={key} className="flex items-center justify-between px-3 py-2 rounded-md bg-muted/40 text-sm">
                          <span className="text-muted-foreground">{label}</span>
                          <span className={`font-semibold ${color}`}>
                            {(stats.events[key] ?? 0).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* User breakdown */}
                {stats?.users && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Users</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {Object.entries(stats.users.by_role || {}).map(([role, count]) => (
                        <div key={role} className="flex items-center justify-between px-3 py-2 rounded-md bg-muted/40 text-sm">
                          <span className="text-muted-foreground capitalize">{role}</span>
                          <span className="font-semibold">{count.toLocaleString()}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between px-3 py-2 rounded-md bg-muted/40 text-sm">
                        <span className="text-muted-foreground">Interactions</span>
                        <span className="font-semibold">{(stats.users.total_interactions ?? 0).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                )}

                {stats && <TranslationCoverage latest={stats.translation_coverage} />}

                {stats?.timestamp && (
                  <p className="text-xs text-muted-foreground">
                    Last report: {new Date(stats.timestamp).toLocaleString("en-US")}
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Admin Actions */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              <CardTitle>Admin Actions</CardTitle>
            </div>
            <CardDescription>These actions run background processes that may take several minutes. Use with caution.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {actionMessage && (
              <div className="p-3 rounded-md text-sm bg-green-500/10 text-green-700 border border-green-500/20">
                {actionMessage}
                {pipelineMode === "n8n" && pipelineExecutionUrl && (
                  <>
                    {" "}
                    <a
                      href={pipelineExecutionUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="underline font-medium"
                    >
                      Open execution{pipelineExecutionId ? ` #${pipelineExecutionId}` : ""}
                    </a>
                  </>
                )}
                <button className="ml-2 underline" onClick={() => setActionMessage(null)}>Close</button>
              </div>
            )}
            {actionError && (
              <div className="p-3 rounded-md text-sm bg-red-500/10 text-red-700 border border-red-500/20">
                {actionError}
                <button className="ml-2 underline" onClick={() => setActionError(null)}>Close</button>
              </div>
            )}

            {/* Run Pipeline */}
            <div className="flex flex-col gap-4 rounded-lg border border-border p-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0 flex-1">
                <h4 className="font-medium flex items-center gap-2">
                  <Play className="h-4 w-4" />
                  Run Pipeline
                </h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Fetch events, deduplicate, enrich, and promote in one go.
                  {pipelineMode === "n8n" && (
                    <span className="ml-1 text-blue-600 font-medium">
                      Running via n8n.
                      {pipelineExecutionUrl && (
                        <>
                          {" "}
                          <a
                            href={pipelineExecutionUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="underline"
                          >
                            Open execution{pipelineExecutionId ? ` #${pipelineExecutionId}` : ""}
                          </a>
                        </>
                      )}
                    </span>
                  )}
                  {pipelineMode === "api" && pipelineJobId && (
                    <span className="ml-1 text-yellow-700 font-medium">Running directly (job {pipelineJobId.slice(0, 8)}…).</span>
                  )}
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row lg:shrink-0">
                <Button
                  variant="outline"
                  onClick={handleRunQualityCheck}
                  disabled={isQcRunning || isPipelineRunning}
                >
                  {isQcRunning ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Running checks…</>
                  ) : (
                    <><CheckCircle2 className="h-4 w-4 mr-2" />Run Quality Checks</>
                  )}
                </Button>
                <Button onClick={handleRunPipeline} disabled={isPipelineRunning || isQcRunning}>
                  {isPipelineRunning ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Running…</>
                  ) : (
                    <><Play className="h-4 w-4 mr-2" />Run Pipeline</>
                  )}
                </Button>
              </div>
            </div>

            {/* Cleanup */}
            <CleanupAction onMessage={setActionMessage} onError={setActionError} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
